import { gmailMailboxUnavailableCode } from "@sp-crm/core";
import { InlineBanner } from "components/ui/inline-banner";
import { defaultLinkStyle } from "components/ui/link";
import * as React from "react";
import { Link } from "react-router-dom";
import { breakNewlines } from "util/text";

export interface DeliverySummary {
    succeeded: boolean;
    rejected: string[];
    authError: boolean;
    providerErrorCode?: string | null;
}

export type DeliveryOutcome =
    | { kind: "auth-error" }
    | { kind: "mailbox-unavailable" }
    | { kind: "gmail-mailbox-unavailable" }
    | { kind: "partial"; rejected: string[] }
    | { kind: "all-rejected"; rejected: string[] }
    | { kind: "success" }
    | { kind: "unknown-error" };

export const describeDeliveryOutcome = (summary: DeliverySummary): DeliveryOutcome => {
    if (summary.authError) {
        return { kind: "auth-error" };
    }
    if (summary.providerErrorCode === "MailboxNotEnabledForRESTAPI") {
        return { kind: "mailbox-unavailable" };
    }
    if (summary.providerErrorCode === gmailMailboxUnavailableCode) {
        return { kind: "gmail-mailbox-unavailable" };
    }
    if (summary.rejected.length > 0 && summary.succeeded) {
        return { kind: "partial", rejected: summary.rejected };
    }
    if (summary.rejected.length > 0) {
        return { kind: "all-rejected", rejected: summary.rejected };
    }
    if (summary.succeeded) {
        return { kind: "success" };
    }
    return { kind: "unknown-error" };
};

export const authErrorTitle = "Update your email credentials";
const authErrorBodyBeforeLink =
    "Sending this email failed because the configured credentials for your email provider need to be updated. Navigate to your ";
const authErrorSettingsLinkText = "email settings page";
const authErrorBodyAfterLink = ", reconnect your email provider, and try again.";
export const authErrorBody = `${authErrorBodyBeforeLink}${authErrorSettingsLinkText}${authErrorBodyAfterLink}`;

export const mailboxUnavailableTitle = "Your Outlook mailbox is unavailable";
export const mailboxUnavailableBody =
    "Ask your Microsoft 365 administrator to check that the intended account has an active mailbox accessible to connected apps. Once access is restored, retry the failed email. To use another account, disconnect Outlook in Settings > Account > Email and connect that account. Reconnecting alone may not fix the mailbox.";

export const gmailMailboxUnavailableTitle =
    "Gmail isn't turned on for your Google account";
export const gmailMailboxUnavailableBody =
    "Google won't send email from the account connected in Settings > Account > Email because Gmail isn't turned on for it. This happens with Google accounts created under a non-Gmail address, and with Google Workspace accounts where Gmail is off. Ask your Google Workspace administrator to turn on Gmail for this account, or disconnect it and connect an account that has Gmail, then retry the failed email. Reconnecting the same account won't fix it.";

export const partialSendTitle = "Some recipients were skipped.";
export const partialSendBody = (rejected: string[]): string =>
    `Your email was sent, but the recipients below were skipped because their addresses aren't valid email addresses. Correct them on the contact record so they are included next time.\n\n${rejected.join(
        "\n",
    )}\n\nThere is no need to resend this email.`;

export const allRejectedTitle = "Some emails failed to send.";
export const allRejectedBody = (rejected: string[]): string =>
    `Sending this email to the recipients below failed, likely due to a formatting issue with the email address.\n\n${rejected.join(
        "\n",
    )}`;
export const allRejectedBannerAdvice = "Update those email addresses and try again.";
export const allRejectedRetryPrompt =
    "Would you like to go back to the email and try again?";

export const unknownErrorTitle = "Sorry, something went wrong";
export const unknownErrorBody =
    "Sorry, something went wrong! Please try reloading the page to fix the issue.";

export const undeliverableRecipientsBody = (addresses: string[]): string =>
    `These recipients can't receive email because their addresses aren't valid email addresses. Remove them, or correct them on the contact record before sending.\n\n${addresses.join(
        "\n",
    )}`;

export const DeliveryOutcomeBanner = (props: {
    outcome: DeliveryOutcome;
}): JSX.Element | null => {
    const { outcome } = props;
    switch (outcome.kind) {
        case "auth-error":
            return (
                <InlineBanner type="error" showIcon={false}>
                    {authErrorBodyBeforeLink}
                    <Link className={defaultLinkStyle} to="/settings/account/email">
                        {authErrorSettingsLinkText}
                    </Link>
                    {authErrorBodyAfterLink}
                </InlineBanner>
            );
        case "mailbox-unavailable":
            return (
                <InlineBanner type="error" showIcon={false}>
                    <strong>{mailboxUnavailableTitle}.</strong> {mailboxUnavailableBody}
                </InlineBanner>
            );
        case "gmail-mailbox-unavailable":
            return (
                <InlineBanner type="error" showIcon={false}>
                    <strong>{gmailMailboxUnavailableTitle}.</strong>{" "}
                    {gmailMailboxUnavailableBody}
                </InlineBanner>
            );
        case "partial":
            return (
                <InlineBanner type="warning" showIcon={false}>
                    {breakNewlines(partialSendBody(outcome.rejected))}
                </InlineBanner>
            );
        case "all-rejected":
            return (
                <InlineBanner type="error" showIcon={false}>
                    {breakNewlines(
                        `${allRejectedBody(outcome.rejected)}\n\n${allRejectedBannerAdvice}`,
                    )}
                </InlineBanner>
            );
        case "unknown-error":
            return (
                <InlineBanner type="error" showIcon={false}>
                    {unknownErrorBody}
                </InlineBanner>
            );
        case "success":
            return null;
        default: {
            const exhaustiveCheck: never = outcome;
            throw new RangeError(
                `Expected ${exhaustiveCheck} to be matched, but wasn't.`,
            );
        }
    }
};
